import { z } from 'zod';
import { GitCiStatus, GitReviewState, GitTimeMeta } from './git.js';
/**
 * Tab Git — dấu MR trên **thẻ Kanban** (APM-GIT-i, phần web).
 * docs/plan-git-integration.md §8.2 câu cuối: "một biểu tượng nhỏ trạng thái MR +
 * chấm CI khi task có MR đang mở".
 *
 * Một bảng có vài trăm thẻ; hỏi `GET …/git/links` cho từng thẻ là vài trăm lời
 * gọi. Endpoint `GET …/git/task-marks` trả **một** bản đồ cho cả dự án, và chỉ
 * mang đúng thứ thẻ vẽ — không tiêu đề, không tên nhánh, không tên người.
 *
 * Tệp riêng (không nối vào `git.ts`) để các task `APM-GIT-*` chạy song song không
 * giẫm lên nhau.
 */
/** Số MR đang mở đọc cho một lượt (mới cập nhật trước); vượt ⇒ `truncated`. */
export const GIT_TASK_MARKS_MAX_CHANGE_REQUESTS = 500;
/** Xấu nhất trước: thứ cần người nhìn vào thắng thứ đã ổn. */
const CI_SEVERITY = {
    failed: 5,
    running: 4,
    pending: 3,
    canceled: 2,
    success: 1,
    none: 0,
};
/**
 * CI **xấu nhất** trong một nhóm MR: `failed` > `running` > `pending` >
 * `canceled` > `success` > `none`. Một chấm xanh trên thẻ phải nghĩa là *mọi* MR
 * của việc đều xanh — nên một MR đỏ, hay còn đang chạy, thắng các MR đã đạt.
 * Nhóm rỗng ⇒ `none`.
 */
export function worstGitCi(statuses) {
    let worst = 'none';
    for (const status of statuses) {
        if (CI_SEVERITY[status] > CI_SEVERITY[worst])
            worst = status;
    }
    return worst;
}
/**
 * Trạng thái review gộp của một nhóm MR — thứ **đang chờ người** thắng:
 * có MR bị yêu cầu sửa ⇒ `changes_requested`; không thì có MR chờ review ⇒
 * `requested`; `approved` chỉ khi **mọi** MR đã được duyệt; còn lại `none`.
 */
export function combineGitReview(states) {
    if (states.includes('changes_requested'))
        return 'changes_requested';
    if (states.includes('requested'))
        return 'requested';
    return states.length > 0 && states.every((state) => state === 'approved') ? 'approved' : 'none';
}
/** Dấu của **một task**: gộp mọi MR đang mở có liên kết hiệu lực tới nó. */
export const GitTaskMark = z.object({
    /** Số MR đang mở (kể cả nháp) của việc — luôn ≥ 1: việc không có MR mở thì không có dấu. */
    openChangeRequests: z.number().int().positive(),
    /** Mọi MR đang mở của việc đều còn là nháp. */
    allDraft: z.boolean(),
    /** `worstGitCi` của các MR ấy; `none` = chưa MR nào có CI (không vẽ chấm). */
    worstCi: GitCiStatus,
    /** `combineGitReview` của các MR ấy. */
    reviewState: GitReviewState,
});
/** Gộp các MR đang mở của một việc thành dấu; không MR nào ⇒ `null`. */
export function summarizeGitTaskMark(changeRequests) {
    if (changeRequests.length === 0)
        return null;
    return {
        openChangeRequests: changeRequests.length,
        allDraft: changeRequests.every((changeRequest) => changeRequest.isDraft),
        worstCi: worstGitCi(changeRequests.map((changeRequest) => changeRequest.ciStatus)),
        reviewState: combineGitReview(changeRequests.map((changeRequest) => changeRequest.reviewState)),
    };
}
/**
 * `GET …/git/task-marks` — id task → dấu, **chỉ** cho task có MR đang mở tại
 * `asOf` (`stateAt`). Task không có trong bản đồ = không vẽ gì.
 *
 * "Có MR" = liên kết **hiệu lực** (§4.2), cả `closes` lẫn `mentions`, đúng tập mà
 * khối "Phát triển" của việc ấy liệt kê — dấu trên thẻ không được trỏ tới thứ mở
 * việc ra không thấy. Chỉ task: issue vận hành không nằm trên bảng Kanban.
 *
 * ⚠️ Thứ **không** dựng lại được ở `asOf` (như `GitChangeRequestView`): nháp hay
 * không, CI, review luôn là giá trị hiện tại của MR.
 */
export const GitTaskMarks = GitTimeMeta.extend({
    marks: z.record(z.uuid(), GitTaskMark),
    /** Dự án có hơn `GIT_TASK_MARKS_MAX_CHANGE_REQUESTS` MR đang mở — dấu chỉ tính trên số mới cập nhật nhất. */
    truncated: z.boolean(),
});
//# sourceMappingURL=git-task-marks.js.map